// src/pages/fleet/reports/columns.tsx — column sets shared by report pages and the
// Overview pop-ups, so a table looks the same wherever it appears.
import React from 'react';
import type {
  TimeVerdict, TripPerformanceRow,
  AlertReportRow, DeviceHealthRow, OverspeedRow, StoppageRow, TripReportRow, VehicleSummaryRow,
} from '../../../services/fleetApi';
import {
  RCol, Tag, fmtDT, fmtDur, fmtKm, humanize, mapsLink, TRIP_TONE, HEALTH_TONE, SEVERITY_TONE,
} from './reportKit';

type Iso = string | null | undefined;
/** A date column: shows "22 Sept 2026, 10:15 am", sorts chronologically, exports the formatted text. */
const dateCol = <R,>(key: string, label: string, get: (r: R) => Iso): RCol<R> => ({
  key, label, value: (r) => fmtDT(get(r)), sortValue: (r) => get(r) || '', render: (r) => <span style={{ whiteSpace: 'nowrap' }}>{fmtDT(get(r))}</span>,
});
const bold = (s: string) => <b style={{ whiteSpace: 'nowrap' }}>{s}</b>;
const nowrap = (s: string) => <span style={{ whiteSpace: 'nowrap' }}>{s}</span>;
const placeCol = <R extends { latitude: number | null; longitude: number | null }>(): RCol<R> => ({
  key: 'place', label: 'Location',
  value: (r) => (r.latitude == null ? '' : `${r.latitude}, ${r.longitude}`),
  render: (r) => {
    const href = mapsLink(r.latitude, r.longitude);
    return href ? <a href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--fx-accent)', whiteSpace: 'nowrap' }}>📍 {r.latitude}, {r.longitude}</a> : '—';
  },
});

export const TRIP_COLS: RCol<TripReportRow>[] = [
  { key: 'tripNumber', label: 'Trip No.', render: (r) => bold(r.tripNumber) },
  { key: 'vehicleNumber', label: 'Vehicle', render: (r) => <span style={{ whiteSpace: 'nowrap' }}>{r.vehicleNumber}</span> },
  { key: 'driverName', label: 'Driver' },
  { key: 'fromLocation', label: 'From', render: (r) => <span title={r.fromLocation} style={ellipsis}>{r.fromLocation}</span> },
  { key: 'toLocation', label: 'To', render: (r) => <span title={r.toLocation} style={ellipsis}>{r.toLocation}</span> },
  { key: 'status', label: 'Status', value: (r) => humanize(r.status), render: (r) => <Tag text={humanize(r.status)} tone={TRIP_TONE[r.status] || 'gray'} /> },
  dateCol<TripReportRow>('startedAt', 'Started', (r) => r.startedAt),
  dateCol<TripReportRow>('endedAt', 'Ended', (r) => r.endedAt),
  { key: 'durationMin', label: 'Duration', align: 'right', value: (r) => fmtDur(r.durationMin), sortValue: (r) => r.durationMin ?? -1 },
  { key: 'plannedKm', label: 'Planned', align: 'right', value: (r) => fmtKm(r.plannedKm), sortValue: (r) => r.plannedKm },
  { key: 'distanceKm', label: 'Travelled', align: 'right', value: (r) => fmtKm(r.distanceKm), sortValue: (r) => r.distanceKm },
  { key: 'quantityMt', label: 'Qty (MT)', align: 'right', value: (r) => r.quantityMt ?? '—', sortValue: (r) => r.quantityMt ?? -1 },
  { key: 'invoiceNumber', label: 'Invoice', value: (r) => r.invoiceNumber || '—' },
  { key: 'alerts', label: 'Alerts', align: 'right' },
];

export const VEHICLE_COLS: RCol<VehicleSummaryRow>[] = [
  { key: 'vehicleNumber', label: 'Vehicle', render: (r) => bold(r.vehicleNumber) },
  { key: 'driverName', label: 'Driver' },
  { key: 'trips', label: 'Trips', align: 'right' },
  { key: 'completedTrips', label: 'Completed', align: 'right' },
  { key: 'distanceKm', label: 'Distance', align: 'right', value: (r) => fmtKm(r.distanceKm), sortValue: (r) => r.distanceKm },
  { key: 'runningMin', label: 'Running', align: 'right', value: (r) => fmtDur(r.runningMin), sortValue: (r) => r.runningMin },
  { key: 'haltMin', label: 'Halted', align: 'right', value: (r) => fmtDur(r.haltMin), sortValue: (r) => r.haltMin },
  { key: 'avgSpeed', label: 'Avg Speed', align: 'right', value: (r) => (r.avgSpeed ? `${r.avgSpeed} km/h` : '—'), sortValue: (r) => r.avgSpeed },
  { key: 'maxSpeed', label: 'Max Speed', align: 'right', value: (r) => (r.maxSpeed ? `${r.maxSpeed} km/h` : '—'), sortValue: (r) => r.maxSpeed },
  { key: 'pings', label: 'GPS Points', align: 'right' },
];

export const STOPPAGE_COLS: RCol<StoppageRow>[] = [
  { key: 'vehicleNumber', label: 'Vehicle', render: (r) => bold(r.vehicleNumber) },
  { key: 'tripNumber', label: 'Trip No.', render: (r) => nowrap(r.tripNumber) },
  dateCol<StoppageRow>('startAt', 'Stopped At', (r) => r.startAt),
  dateCol<StoppageRow>('endAt', 'Moved At', (r) => r.endAt),
  { key: 'durationMin', label: 'Duration', align: 'right', value: (r) => fmtDur(r.durationMin), sortValue: (r) => r.durationMin,
    render: (r) => <b style={{ color: r.durationMin >= 120 ? 'var(--fx-red)' : r.durationMin >= 45 ? 'var(--fx-orange)' : 'var(--fx-text)' }}>{fmtDur(r.durationMin)}</b> },
  placeCol<StoppageRow>(),
];

export const OVERSPEED_COLS: RCol<OverspeedRow>[] = [
  { key: 'vehicleNumber', label: 'Vehicle', render: (r) => bold(r.vehicleNumber) },
  { key: 'driverName', label: 'Driver' },
  { key: 'tripNumber', label: 'Trip No.', render: (r) => nowrap(r.tripNumber) },
  dateCol<OverspeedRow>('startAt', 'From', (r) => r.startAt),
  dateCol<OverspeedRow>('endAt', 'To', (r) => r.endAt),
  { key: 'durationMin', label: 'Duration', align: 'right', value: (r) => fmtDur(r.durationMin), sortValue: (r) => r.durationMin },
  { key: 'maxSpeed', label: 'Top Speed', align: 'right', value: (r) => `${r.maxSpeed} km/h`, sortValue: (r) => r.maxSpeed,
    render: (r) => <b style={{ color: 'var(--fx-red)' }}>{r.maxSpeed} km/h</b> },
  { key: 'avgSpeed', label: 'Avg Speed', align: 'right', value: (r) => `${r.avgSpeed} km/h`, sortValue: (r) => r.avgSpeed },
  placeCol<OverspeedRow>(),
];

export const ALERT_COLS: RCol<AlertReportRow>[] = [
  dateCol<AlertReportRow>('createdAt', 'Time', (r) => r.createdAt),
  { key: 'vehicleNumber', label: 'Vehicle', render: (r) => bold(r.vehicleNumber) },
  { key: 'tripNumber', label: 'Trip No.', render: (r) => nowrap(r.tripNumber) },
  { key: 'type', label: 'Type', value: (r) => humanize(r.type), render: (r) => nowrap(humanize(r.type)) },
  { key: 'severity', label: 'Severity', value: (r) => humanize(r.severity), render: (r) => <Tag text={humanize(r.severity)} tone={SEVERITY_TONE[r.severity] || 'gray'} /> },
  { key: 'message', label: 'Message', render: (r) => <span style={{ ...ellipsis, maxWidth: 320 }} title={r.message}>{r.message}</span> },
  { key: 'status', label: 'Status', render: (r) => <Tag text={r.status} tone={r.status === 'Open' ? 'red' : 'green'} /> },
  dateCol<AlertReportRow>('resolvedAt', 'Resolved At', (r) => r.resolvedAt),
];

export const DEVICE_COLS: RCol<DeviceHealthRow>[] = [
  { key: 'vehicleNumber', label: 'Vehicle', render: (r) => bold(r.vehicleNumber) },
  { key: 'gpsDeviceId', label: 'GPS Device', value: (r) => [r.gpsDeviceId || 'Not linked', r.deviceImei].filter(Boolean).join(' · '),
    render: (r) => (r.gpsDeviceId
      ? <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, whiteSpace: 'nowrap' }}>
          {r.gpsDeviceId}{r.deviceImei ? <><br /><span style={{ color: 'var(--fx-text-3)' }}>IMEI {r.deviceImei}</span></> : null}
        </span>
      : <span style={{ color: 'var(--fx-orange)', fontWeight: 600 }}>Not linked</span>) },
  { key: 'vendorName', label: 'Transporter', value: (r) => r.vendorName || '—',
    render: (r) => <span title={r.vendorCode} style={{ ...ellipsis, maxWidth: 160 }}>{r.vendorName || '—'}</span> },
  { key: 'healthLabel', label: 'Health', render: (r) => <Tag text={r.healthLabel} tone={HEALTH_TONE[r.healthStatus] || 'gray'} /> },
  { key: 'devicePowerOn', label: 'Power', value: (r) => (r.devicePowerOn == null ? '—' : r.devicePowerOn ? 'Connected' : 'Disconnected'),
    render: (r) => (r.devicePowerOn == null
      ? <span style={{ color: 'var(--fx-text-3)' }}>—</span>
      : <Tag text={r.devicePowerOn ? 'Connected' : 'Unplugged'} tone={r.devicePowerOn ? 'green' : 'red'} />) },
  { key: 'tripStatus', label: 'Trip Status', value: (r) => humanize(r.tripStatus) },
  dateCol<DeviceHealthRow>('lastGpsAt', 'Last GPS Fix', (r) => r.lastGpsAt),
  { key: 'gpsAgeMinutes', label: 'GPS Age', align: 'right', value: (r) => fmtDur(r.gpsAgeMinutes), sortValue: (r) => r.gpsAgeMinutes ?? Infinity },
  { key: 'dataDelayMinutes', label: 'Data Delay', align: 'right', value: (r) => fmtDur(r.dataDelayMinutes), sortValue: (r) => r.dataDelayMinutes ?? -1 },
  { key: 'speed', label: 'Speed', align: 'right', value: (r) => `${r.speed} km/h`, sortValue: (r) => r.speed },
  { key: 'openIssues', label: 'Open Issues', align: 'right' },
  { key: 'lastIssue', label: 'Last Issue', render: (r) => <span style={{ ...ellipsis, maxWidth: 260 }} title={r.lastIssue}>{r.lastIssue || '—'}</span> },
];

const ellipsis: React.CSSProperties = { display: 'inline-block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' };

// ── Trip performance: route deviation + time vs estimate ─────────────────────
export const VERDICT: Record<TimeVerdict, { label: string; tone: 'green' | 'orange' | 'red' | 'gray' | 'blue' }> = {
  ON_TIME: { label: 'On Time', tone: 'green' },
  EARLY: { label: 'Early', tone: 'blue' },
  LATE: { label: 'Late', tone: 'red' },
  RUNNING_LATE: { label: 'Running Late', tone: 'red' },
  ON_TRACK: { label: 'On Track', tone: 'green' },
  NOT_STARTED: { label: 'Not Started', tone: 'gray' },
  NOT_STARTED_LATE: { label: 'Start Overdue', tone: 'orange' },
  NO_ESTIMATE: { label: 'No Estimate', tone: 'gray' },
  CANCELLED: { label: 'Cancelled', tone: 'gray' },
};
/** "+2h 05m" (took longer) / "−30m" (quicker) / "On plan". */
export const fmtExtra = (m: number | null | undefined) =>
  m == null ? '—' : m === 0 ? 'On plan' : `${m > 0 ? '+' : '−'}${fmtDur(Math.abs(m))}`;
const extraColor = (m: number | null) => (m == null ? 'var(--fx-text-3)' : m > 30 ? 'var(--fx-red)' : m < -30 ? 'var(--fx-accent)' : 'var(--fx-green)');

export const PERF_COLS: RCol<TripPerformanceRow>[] = [
  { key: 'tripNumber', label: 'Trip No.', render: (r) => bold(r.tripNumber) },
  { key: 'vehicleNumber', label: 'Vehicle', render: (r) => nowrap(r.vehicleNumber) },
  { key: 'toLocation', label: 'To', render: (r) => <span title={r.toLocation} style={ellipsis}>{r.toLocation}</span> },
  { key: 'deviations', label: 'Deviations', align: 'right',
    render: (r) => (r.deviations
      ? <b style={{ color: 'var(--fx-red)' }}>{r.deviations}{r.deviationOpen && r.status === 'ACTIVE' ? ' · now' : ''}</b>
      : <span style={{ color: 'var(--fx-green)' }}>0</span>) },
  { key: 'deviationKm', label: 'Off Route', align: 'right', value: (r) => (r.deviationKm ? fmtKm(r.deviationKm) : '—'), sortValue: (r) => r.deviationKm ?? -1 },
  { key: 'deviationMin', label: 'Time Off Route', align: 'right', value: (r) => (r.deviationMin ? fmtDur(r.deviationMin) : '—'), sortValue: (r) => r.deviationMin ?? -1 },
  dateCol<TripPerformanceRow>('estEnd', 'Est. Arrival', (r) => r.estEnd),
  dateCol<TripPerformanceRow>('completedAt', 'Arrived', (r) => r.completedAt),
  { key: 'estimatedMin', label: 'Est. Time', align: 'right', value: (r) => fmtDur(r.estimatedMin), sortValue: (r) => r.estimatedMin ?? -1 },
  { key: 'actualMin', label: 'Actual Time', align: 'right', value: (r) => fmtDur(r.actualMin), sortValue: (r) => r.actualMin ?? -1 },
  { key: 'extraMin', label: 'Difference', align: 'right', value: (r) => fmtExtra(r.extraMin), sortValue: (r) => r.extraMin ?? -99999,
    render: (r) => <b style={{ color: extraColor(r.extraMin), whiteSpace: 'nowrap' }}>{fmtExtra(r.extraMin)}</b> },
  { key: 'verdict', label: 'Result', value: (r) => VERDICT[r.verdict].label, render: (r) => <Tag text={VERDICT[r.verdict].label} tone={VERDICT[r.verdict].tone} /> },
];

const pick = <R,>(cols: RCol<R>[], keys: string[]) => keys.map((k) => cols.find((c) => c.key === k)).filter((c): c is RCol<R> => !!c);
/** Pop-up focused on route deviation. */
export const DEVIATION_COLS = pick(PERF_COLS, ['tripNumber', 'vehicleNumber', 'toLocation', 'deviations', 'deviationKm', 'deviationMin', 'verdict']);
/** Pop-up focused on time vs estimate. */
export const TIME_COLS = pick(PERF_COLS, ['tripNumber', 'vehicleNumber', 'toLocation', 'estEnd', 'completedAt', 'estimatedMin', 'actualMin', 'extraMin', 'verdict']);
